import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  ACTIVE_GROUP_EVENT,
  getActiveGroupId,
  setActiveGroupId as setActiveGroupIdUtil,
} from "./activeGroup";
import { Group, User, Tenant } from "@/generated";
import { DATA_KEYS, useRemoteData } from "./useRemoteData";
import { NoxusLoadingScreen } from "@/components/UI/NoxusLoadingScreen";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { CircleX } from "lucide-react";
import { EnvService } from "@/services/Environment/Environment";
import { keepPreviousData } from "@tanstack/react-query";
import { usePermissions } from "@/hooks/usePermissions";
import { ORG_PERMISSIONS } from "@/constants/permissions";
import { setCache, clearCache, CACHE_KEYS } from "@/utils/queryCache";
import { ACTIVE_GROUP_KEY } from "./activeGroup";
import { isNoxusInternal } from "./isNoxusInternal";

type ISpotUser = {
  spotUser: User;
  spotTenant: Tenant;
  spotGroup: Group;
  isLoading: boolean;
  isGhosting: boolean;
  isOnPrem: boolean;
  isLocalNoxus: boolean;
  setActiveGroupId: (groupId: string) => void;
};

const spotUserDefault: ISpotUser = {
  spotUser: {
    id: "",
    tenant_id: "",
    auth_id: "",
    domain: "",
    display_name: "",
    image: "",
    settings: {},
    is_admin: false,
    is_active: false,
    role: "",
    is_pending: false,
    email: "",
    created_at: "",
    last_login: "",
  },
  spotGroup: {
    id: "",
    name: "",
    description: "",
    created_at: "",
    settings: null,
  },
  spotTenant: {
    id: "",
    name: "",
    domain: "",
    is_active: false,
    created_at: "",
    stripe_id: "",
    subscription_plan: "",
    automatic_recharge_amount: 0,
    current_credits: 0,
    logo: null,
  },
  isLoading: true,
  isGhosting: false,
  isOnPrem: false,
  isLocalNoxus: false,
  setActiveGroupId: () => {},
};

const SpotUserContext = createContext(spotUserDefault);

export function useSpotUser() {
  return useContext(SpotUserContext);
}

/**
 * Detects when the active workspace is one the user no longer has access to
 * (e.g. removed after a migration) and silently switches them to a valid one.
 *
 * Must be rendered inside SpotUserContext.Provider so useSpotUser() and
 * usePermissions() resolve against the real context rather than the default.
 * null group_permissions means the backend confirmed no access and no org:admin
 * override — so we pick the first workspace the user actually belongs to.
 */
function WorkspaceFallbackGuard() {
  const { setActiveGroupId } = useSpotUser();
  const { permissions } = usePermissions();
  const { data: spotGroups } = useRemoteData(DATA_KEYS.MY_GROUPS);

  const firstMemberGroupId = spotGroups?.items?.[0]?.group.id;

  useEffect(() => {
    if (
      permissions !== undefined &&
      permissions.group_permissions === null &&
      firstMemberGroupId
    ) {
      setActiveGroupId(firstMemberGroupId);
    }
  }, [permissions, firstMemberGroupId, setActiveGroupId]);

  return null;
}

export function SpotUserProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const isOnPrem = EnvService.getVar("ON_PREM") === "true";
  const [activeGroupId, setActiveGroupIdState] =
    useState<string>(getActiveGroupId);
  const pathname = usePathname();
  const { hasPermission } = usePermissions();

  // Sync React state when setActiveGroupId is called from outside React (e.g. api-client)
  useEffect(() => {
    const handler = (e: Event) => {
      setActiveGroupIdState((e as CustomEvent<string>).detail);
    };
    window.addEventListener(ACTIVE_GROUP_EVENT, handler);
    return () => window.removeEventListener(ACTIVE_GROUP_EVENT, handler);
  }, []);

  const setActiveGroupId = useCallback((groupId: string) => {
    setActiveGroupIdUtil(groupId); // writes sessionStorage + dispatches event
  }, []);

  /* Noxus tenant */
  const {
    data: spotTenant,
    isError: isErrorTenant,
    isLoading: isLoadingTenant,
  } = useRemoteData(
    DATA_KEYS.TENANT,
    {},
    {
      placeholderData: keepPreviousData,
      enabled: !pathname?.includes("/public/"),
    }
  );

  /* Noxus user */
  const {
    data: spotUser,
    isLoading: isLoadingUser,
    isError: isErrorUser,
  } = useRemoteData(
    DATA_KEYS.USER,
    {},
    {
      placeholderData: keepPreviousData,
      enabled: !pathname?.includes("/public/"),
    }
  );

  const { data: spotGroups } = useRemoteData(DATA_KEYS.MY_GROUPS);
  const { data: allGroups } = useRemoteData(DATA_KEYS.ALL_GROUPS, undefined, {
    enabled:
      hasPermission(ORG_PERMISSIONS.ORG.ADMIN, "tenant") &&
      spotGroups?.items?.length === 0,
  });

  // Compute a default group from backend data (user settings → first available group).
  const backendDefaultGroup = useMemo(() => {
    if (!spotUser) return "";
    const saved = spotUser.settings?.defaultGroup as string | undefined;
    const groups = spotGroups?.items?.length
      ? spotGroups.items
      : (allGroups?.items ?? []);
    const firstAvailable = groups[0]?.group.id as string | undefined;
    if (saved && groups.length > 0) {
      const exists = groups.some((item) => item.group.id === saved);
      if (exists) return saved;
      return firstAvailable ?? "";
    }
    return saved || firstAvailable || "";
  }, [spotUser, spotGroups, allGroups]);

  // Persist to sessionStorage when we resolve a default (so subsequent renders
  // and tabs pick it up immediately), but don't wait for the effect to re-render.
  useEffect(() => {
    if (!activeGroupId && backendDefaultGroup) {
      setActiveGroupId(backendDefaultGroup);
    }
  }, [activeGroupId, backendDefaultGroup, setActiveGroupId]);

  // Use the backend default synchronously when sessionStorage is empty,
  // so the GROUP query fires immediately without waiting for the useEffect round-trip.
  const effectiveGroupId = activeGroupId || backendDefaultGroup;

  /* Noxus group */
  const {
    data: spotGroup,
    isLoading: isLoadingGroup,
    isError: isErrorGroup,
  } = useRemoteData(
    DATA_KEYS.GROUP,
    {
      path: {
        group_id: effectiveGroupId,
      },
    },
    {
      placeholderData: keepPreviousData,
      enabled:
        Boolean(spotUser) &&
        !pathname?.includes("/public/") &&
        !!effectiveGroupId,
      retry: false,
    }
  );

  // If the GROUP query fails (403/404 — stale sessionStorage from another tenant),
  // clear the bad ID and fall back to the backend default.
  useEffect(() => {
    if (
      isErrorGroup &&
      activeGroupId &&
      backendDefaultGroup &&
      activeGroupId !== backendDefaultGroup
    ) {
      setActiveGroupId(backendDefaultGroup);
    }
  }, [isErrorGroup, activeGroupId, backendDefaultGroup, setActiveGroupId]);

  // Still loading if we have a group ID to fetch but haven't received the data yet.
  const isAwaitingGroup =
    !!effectiveGroupId &&
    !spotGroup &&
    !isErrorGroup &&
    !pathname?.includes("/public/");
  const isLoading = useMemo(
    () => isLoadingTenant || isLoadingUser || isLoadingGroup || isAwaitingGroup,
    [isLoadingTenant, isLoadingUser, isLoadingGroup, isAwaitingGroup]
  );

  // Persist to localStorage for stale-while-revalidate on next visit
  useEffect(() => {
    if (spotUser) setCache(CACHE_KEYS.USER, spotUser);
  }, [spotUser]);

  useEffect(() => {
    if (spotTenant) setCache(CACHE_KEYS.TENANT, spotTenant);
  }, [spotTenant]);

  useEffect(() => {
    if (spotGroup) setCache(CACHE_KEYS.GROUP, spotGroup);
  }, [spotGroup]);

  const isGhosting = useMemo(
    () =>
      !!(
        spotUser &&
        spotUser.settings?.original_tenant_id &&
        spotUser.settings?.original_tenant_id != spotUser.tenant_id
      ),
    [spotUser]
  );

  const isPushingToOnboarding =
    !isLoadingUser && !isErrorUser && (!spotUser || !spotUser.id);

  useEffect(() => {
    if (isPushingToOnboarding) {
      // Drain stale cached data from a previous session (e.g. after a DB reset)
      // so the group query isn't blocked by a stale activeGroupId.
      clearCache();
      sessionStorage.removeItem(ACTIVE_GROUP_KEY);

      if (!pathname?.includes("/onboarding")) {
        router.push("/onboarding");
      }
    }
  }, [isPushingToOnboarding, router, pathname]);

  const hasHandledUtmTracking = useRef(false);

  // UTM tracking when user is available
  useEffect(() => {
    if (spotUser?.email && !hasHandledUtmTracking.current) {
      const utmSource = localStorage.getItem("utm_source");

      if (utmSource) {
        // Call the UTM tracking API endpoint
        hasHandledUtmTracking.current = true;
        fetch("/api/utm-tracking", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: spotUser.email,
            source: utmSource,
          }),
        })
          .then((response) => {
            if (response.ok) {
              localStorage.removeItem("utm_source");
            }
          })
          .catch((error) => {
            console.error("Error tracking UTM source:", error);
          })
          .finally(() => {
            localStorage.removeItem("utm_source");
          });
      }
    }
  }, [spotUser?.email]);

  // Source of truth: spotUser.email comes from Auth0 (users cannot self-edit it
  // via /users/me PATCH — email is not in UserUpdateProfileRequest). The same
  // internal-domain list is enforced server-side on privileged endpoints
  // (e.g. activate_dev_license), so this flag is defense-in-depth UI gating,
  // not the authorization boundary.
  const email = spotUser?.email;
  const isLocalNoxus = useMemo(
    () => (email ? isNoxusInternal(email) : false),
    [email]
  );

  const value: ISpotUser = {
    spotUser: spotUser ?? spotUserDefault.spotUser,
    spotTenant: spotTenant ?? spotUserDefault.spotTenant,
    spotGroup: spotGroup?.group ?? spotUserDefault.spotGroup,
    isLoading,
    isGhosting,
    isOnPrem,
    isLocalNoxus,
    setActiveGroupId,
  };

  return (
    <SpotUserContext.Provider value={value}>
      <WorkspaceFallbackGuard />
      {(isLoadingUser && !pathname?.includes("/public/")) ||
      (isPushingToOnboarding &&
        pathname &&
        !pathname.includes("/onboarding")) ? (
        <NoxusLoadingScreen />
      ) : isErrorTenant || isErrorUser ? (
        <NoxusEmptyBlock
          icon={CircleX}
          title={"There was an error"}
          description={"It seems there was an error while retrieving data."}
          isScreenSize
          linkButton={"Logout"}
          link={"/auth/logout"}
        />
      ) : (
        children
      )}
    </SpotUserContext.Provider>
  );
}
